import qs from 'qs'
import invariant from 'tiny-invariant'

import type { definitions, paths } from '@luxuryescapes/contract-svc-accommodation'

import request from 'api/requestUtils'
import { split } from 'lib/array/arrayUtils'
import { mergeObjects } from 'lib/object/objectUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

import mapProperty from './mappers/propertyMapper'
import mapPropertyAnytimeRate from './mappers/propertyAnytimeRateMapper'
import mapPropertyCalendarPrices from './mappers/propertyCalendarPricesMapper'
import mapPropertyRates from './mappers/propertyRatesMapper'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'

/* ************ */
/* Get property */
/* ************ */

interface PropertyParams {
  regionCode: string
  currency: string
  previewMode: boolean
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerGetPropertyContent = paths['/api/accommodation/content/properties/{propertyId}']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerGetPropertyContentRequestQueryParams = Omit<ServerGetPropertyContent['parameters']['query'], 'brand'>
type ServerGetPropertyContentResponse = ServerGetPropertyContent['responses']['200']['content']['application/json']

export async function getProperty(
  propertyId: string,
  params: PropertyParams,
): Promise<App.Property.Property | undefined> {
  const queryParams: ServerGetPropertyContentRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    allowMysteryRooms: true,
    allFacilities: true,
    preview: params.previewMode,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerGetPropertyContentResponse>(
    `/api/accommodation/content/properties/${propertyId}?${query}`,
  )
  const serverPropertyContent = response.result
  if (serverPropertyContent) {
    return mapProperty(serverPropertyContent, params.previewMode)
  }
}

/* ************************* */
/* Get property anytime rate */
/* ************************* */

interface PropertyAnytimeRateParams {
  regionCode: string
  currency: string
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerAnytimePricing = paths['/api/accommodation/properties/anytime-pricing']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerAnytimePricingRequestQueryParams = Omit<ServerAnytimePricing['parameters']['query'], 'brand'>
type ServerAnytimePricingResponse = ServerAnytimePricing['responses']['200']['content']['application/json']

export async function getPropertyAnytimeRate(
  propertyId: string,
  params: PropertyAnytimeRateParams,
): Promise<[App.Property.AnytimeRate, App.Property.AnytimePromotionRate | undefined] | undefined> {
  const queryParams: ServerAnytimePricingRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    propertyIds: [propertyId],
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerAnytimePricingResponse>(
    `/api/accommodation/properties/anytime-pricing?${query}`,
  )
  const serverAnytimePricingForProperty = response.result.properties[propertyId]
  if (serverAnytimePricingForProperty) {
    return mapPropertyAnytimeRate(serverAnytimePricingForProperty, params.currency)
  }
}

/* **************************** */
/* Get property calendar prices */
/* **************************** */

interface PropertyCalendarPricesParams {
  /**
   * Inclusive start of month to fetch, in ISO 8601 (`YYYY-MM`)
   *
   * The resultant date range with `endIsoYearMonth` should not span more than a couple of years.
   */
  startIsoYearMonth: string
  /**
   * Inclusive end of month to fetch, in ISO 8601 (`YYYY-MM`)
   *
   * The resultant date range with `startIsoYearMonth` should not span more than a couple of years.
   */
  endIsoYearMonth: string
  /** Length of stay, in nights */
  duration: number
  /** Room occupancies */
  occupancies: Array<App.Occupants>
  /** Requested currency code, in ISO 4217 (e.g. `"AUD"`) */
  currency: string
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerCalendarPrices = paths['/api/accommodation/properties/{propertyId}/calendar-prices']['get']
// NB: There's no `brand` parameter.
type ServerCalendarPricesRequestQueryParams = ServerCalendarPrices['parameters']['query']
type ServerCalendarPricesResponse = ServerCalendarPrices['responses']['200']['content']['application/json']

export async function getPropertyCalendarPrices(
  propertyId: string,
  params: PropertyCalendarPricesParams,
): Promise<{ [isoDate: string]: App.Property.CalendarDay }> {
  invariant(params.startIsoYearMonth <= params.endIsoYearMonth)
  const allIsoYearMonths = []
  // NB: Constructed dates are for the first of the month at midnight UTC.
  const month = new Date(params.startIsoYearMonth)
  const endMonth = new Date(params.endIsoYearMonth)
  // oxlint-disable-next-line no-unmodified-loop-condition
  while (month <= endMonth) {
    // NB: Because we exclusively manipulate UTC dates, we needn't worry about the local time zone
    // and DST transitions.
    allIsoYearMonths.push(month.toISOString().substring(0, 7))
    month.setUTCMonth(month.getUTCMonth() + 1)
  }
  // Accommodation service permits up to 12 months per request.
  const isoYearMonthsChunks = split(allIsoYearMonths, 12)
  const queryChunks: Array<string> = isoYearMonthsChunks.map(isoYearMonths => {
    const queryParams: ServerCalendarPricesRequestQueryParams = {
      los: String(params.duration),
      months: isoYearMonths.join(','),
      currency: params.currency,
      occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    }
    return qs.stringify(queryParams)
  })

  // Two things to keep in mind follow.
  //
  //  1. To keep things simple, if one request fails, we don't bother retrying or salvaging the
  //     other requests.
  //  2. It's on the caller to specify a "reasonable" `startIsoYearMonth` and `endIsoYearMonth`
  //     range that won't lead to an excessive number of requests.
  const mappedChunks = await Promise.all(queryChunks.map(async query => {
    const response = await request.get<ServerCalendarPricesResponse>(
      `/api/accommodation/properties/${propertyId}/calendar-prices?${query}`,
      { excludeBrand: true },
    )
    const serverCalendarPricesResponseResult = response.result
    if (serverCalendarPricesResponseResult) {
      return mapPropertyCalendarPrices(serverCalendarPricesResponseResult)
    }
    return {}
  }))
  return mergeObjects(mappedChunks)
}

/* ****************** */
/* Get property rates */
/* ****************** */

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  previewMode: boolean
  source: string
  dynamicAvailability: boolean
  enablePropertyCollect: boolean
  includeBundles: boolean
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomRateGroupId: string | undefined
  areFlightsEnabled: boolean
  markupSource?: ChannelMarkupSource
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerUnifiedAvailability = paths['/api/accommodation/properties/availability']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerUnifiedAvailabilityRequestQueryParams = Omit<ServerUnifiedAvailability['parameters']['query'], 'brand'>
type ServerUnifiedAvailabilityResponse = ServerUnifiedAvailability['responses']['200']['content']['application/json']
type ServerValidateBundleRate = paths['/api/accommodation/properties/availability/bundles/validate']['post']
type ServerValidateBundleRateRequestBody = Omit<definitions['ValidateBundleRateBody'], 'brand'>
type ServerValidateBundleRateResponse = ServerValidateBundleRate['responses']['200']['content']['application/json']

const GHA_ROOM_RATE_ID_PATTERN = /^(.+?)(?:_([A-Z]{3}))?$/

function parseGhaRoomRateId(roomRateId: string | undefined): [string | undefined, string | undefined] {
  if (!roomRateId) return [undefined, undefined]
  const result = GHA_ROOM_RATE_ID_PATTERN.exec(roomRateId)
  if (!result) return [undefined, undefined]
  return [result[1], result[2]]
}

function mapHighlightedReason(
  utmContent: string | undefined,
): ServerUnifiedAvailabilityRequestQueryParams['highlightedReason'] {
  switch (utmContent) {
    case 'gha-lpc':
    case 'gha-lpp':
      return utmContent
  }
}

export async function getPropertyRates(
  propertyId: string,
  params: PropertyRatesParams,
): Promise<Record<string, Array<App.RawPropertyRoomRates>>> {
  const [highlightedRateId, highlightedRateCurrency] = parseGhaRoomRateId(params.roomRateId)
  const queryParams: ServerUnifiedAvailabilityRequestQueryParams = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    preview: params.previewMode,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    upliftRecommendedRate: true,
    enablePropertyCollect: params.enablePropertyCollect,
    allowMysteryRooms: true,
    includeBundles: params.includeBundles,
    propertyIds: [propertyId],
    highlightedReason: highlightedRateId ? mapHighlightedReason(params.utmContent) : undefined,
    highlightedRoomId: params.roomTypeId,
    highlightedRateId,
    highlightedRateGroupId: params.roomRateGroupId,
    highlightedRateCurrency,
    includeUnavailableResults: true,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerUnifiedAvailabilityResponse>(
    `/api/accommodation/properties/availability?${query}`,
    { credentials: 'include' },
  )
  const serverAvailabilityResponseProperty = response.result.properties[propertyId]
  if (serverAvailabilityResponseProperty) {
    return mapPropertyRates(serverAvailabilityResponseProperty, params.currency, params.areFlightsEnabled, params.markupSource)
  }
  return {}
}

/* ***************************** */
/* Validate property rate bundle */
/* ***************************** */

interface ValidatePropertyRateBundleParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  source: string
  dynamicAvailability: boolean
  propertyId: string
  rateId: string
  /** Mandatory bundle tickets; required alongside `bundleId` */
  tickets?: Array<{ experienceId: string; productId: string; ticketPrice: number }>
  /** Mandatory bundle identifier expected on the rate */
  bundleId?: string
  /**
   * Opt-in bundles the customer has selected. The backend rejects (400) a request carrying both these
   * and a mandatory `bundleId`/`tickets` pair, so callers must issue separate requests.
   */
  optionalBundles?: Array<{
    bundleId: string
    tickets: Array<{ experienceId: string; productId: string; ticketPrice?: number }>
  }>
}

export async function validatePropertyRateBundle(params: ValidatePropertyRateBundleParams): Promise<boolean> {
  const body: ServerValidateBundleRateRequestBody = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    propertyId: params.propertyId,
    rateId: params.rateId,
    tickets: params.tickets,
    bundleId: params.bundleId,
    ...(params.optionalBundles?.length && { optionalBundles: params.optionalBundles }),
  }

  const response = await request.post<ServerValidateBundleRateResponse, ServerValidateBundleRateRequestBody>(
    '/api/accommodation/properties/availability/bundles/validate',
    body,
    { credentials: 'include' },
  )

  return !!response?.result
}
